import React, { useState, useEffect } from 'react'
import { Alert, Input, Password, Button, Modal } from 'components'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { ErrorMessage } from 'store/AuthContext/Types/authSliceType'
import { DataAuthRequest } from 'services/login/interfaces'
import { useDispatch, useSelector } from 'store/store'
import { handleAuth, resetPasswordRequest } from 'store/AuthContext/authSlice'
import { useInterceptors, UseInterceptorsInterface } from 'commons/customHooks'

import { ROUTES, URL_CONST } from 'constants/urls'

import * as S from './styles'
import { authErrors, authRetCod, errorMessages } from 'commons/messages'

import { isObjectEmpty } from 'commons/utils/object'

interface LoginFormProps {
  cd_usua: string
  ds_senh: string
}

const initialError: ErrorMessage = {
  title: '',
  description: ''
}

// TODO: Uncomment when backend is ready
// type LoginType = 'AlpargatasUser' | 'NoAlpargastaUser'

const { forgetPassword } = ROUTES

const LoginForm: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormProps>({
    mode: 'onChange',
    resolver: undefined //isDevelopment ? undefined : yupResolver(loginFormSchema) // TODO: Uncomment when backend is ready
  })

  const [modalExpiredPassword, setModalExpiredPassword] = useState(false)
  const [expirationError, setExpirationError] = useState<ErrorMessage>(initialError)
  // const [userLoginType, setUserLoginType] = useState<LoginType | undefined>(undefined) // TODO: Uncomment when backend is ready

  // TODO: Uncomment when backend is ready
  // const handleSetLoginType = useCallback((loginType: LoginType) => {
  //   setUserLoginType(loginType)
  // }, [])

  const {
    modalTitleErrorMessage,
    modalMessage,
    setModalTitleErrorMessage,
    setModalMessage
  }: UseInterceptorsInterface = useInterceptors()

  const {
    email,
    isLoading,
    loginTries,
    error: loginError
  } = useSelector((state) => state.auth)

  const hasFormError =
    !isObjectEmpty(errors) || watch(['cd_usua', 'ds_senh']).some((item) => !item)

  const prepareValuesToSend = (data: LoginFormProps) => {
    const { cd_usua, ds_senh } = data

    const requestData: DataAuthRequest = {
      cd_usua: cd_usua.trim(),
      ds_senh: ds_senh.trim(),
      nr_cnpj_cpf: 0
    }
    return requestData
  }

  const successCallback = () => navigate(URL_CONST.ROOT)

  const onSubmit = handleSubmit((data) => {
    setModalMessage('...')
    setModalTitleErrorMessage('...')
    setExpirationError(initialError)

    const requestData = prepareValuesToSend(data)
    dispatch(handleAuth(requestData, successCallback))
  })

  const handlePasswordReset = () => {
    const onSend = () => navigate(forgetPassword.getLink('validateCode'))
    dispatch(resetPasswordRequest(email, onSend))
  }

  useEffect(() => {
    if (loginError.title === authErrors[authRetCod.expiredPassword].title) {
      setModalExpiredPassword(true)
    }
  }, [loginError])

  useEffect(() => {
    if (modalTitleErrorMessage === errorMessages.expiredLogin)
      setExpirationError({
        title: modalTitleErrorMessage || '',
        description: modalMessage || ''
      })
  }, [modalTitleErrorMessage, modalMessage])

  const renderErrorAction = () => {
    if (loginTries <= 0) return null
    return (
      <>
        <br />
        <br />
        {loginTries < 3 ? (
          <>
            <strong>Tentativas restantes: </strong>
            {3 - loginTries}
          </>
        ) : (
          <>
            Para desbloquear sua conta,{' '}
            <S.AlertLink onClick={handlePasswordReset}>clique aqui</S.AlertLink> para
            receber um código de redefinição de senha.
          </>
        )}
      </>
    )
  }

  return (
    <S.Container>
      {/*
      TODO: Uncomment when backend is ready
      <S.Title design="hav-h5-text">Seja bem-vindo!</S.Title>
      <S.Subtitle design="hav-default-03-text">
        Para entrar no Portal de Vendas, selecione o seu tipo de usuário:
      </S.Subtitle>

      <S.ButtonsContainer>
        <Button
          fullWidth
          content="Sou funcionário Alpargatas"
          active={userLoginType && userLoginType === 'AlpargatasUser'}
          onClick={() => handleSetLoginType('AlpargatasUser')}
          disabled={isLoading}
        />
        <Button
          fullWidth
          content="Não sou funcionário Alpargatas"
          active={userLoginType === 'NoAlpargastaUser'}
          onClick={() => handleSetLoginType('NoAlpargastaUser')}
          disabled={isLoading}
        />
      </S.ButtonsContainer> */}

      <S.Form onSubmit={onSubmit} autoComplete="on">
        {!!loginError.title && (
          <S.ErrorWrapper>
            <Alert
              kind="error"
              fullWidth
              title={loginError.title}
              message={
                <span>
                  {loginError.description}
                  {renderErrorAction()}
                </span>
              }
            />
          </S.ErrorWrapper>
        )}
        {!!expirationError.title && (
          <S.ErrorWrapper>
            <Alert
              kind="error"
              fullWidth
              title={expirationError.title}
              message={expirationError.description}
            />
          </S.ErrorWrapper>
        )}
        <S.FieldsWrapper>
          <Controller
            name="cd_usua"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Input
                name={name}
                value={value}
                onChange={onChange}
                height={40}
                label="Usuário"
                type="text"
                InputProps={{
                  placeholder: 'Digite seu usuário',
                  autoFocus: true
                }}
                error={errors.cd_usua?.message}
                helperText={errors.cd_usua?.message}
              />
            )}
          />
        </S.FieldsWrapper>
        <S.FieldsWrapper>
          <Controller
            name="ds_senh"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Password
                name={name}
                value={value}
                onChange={onChange}
                height={40}
                label="Senha"
                placeholder="********"
                error={errors.ds_senh?.message}
                helperText={errors.ds_senh?.message}
              />
            )}
          />
        </S.FieldsWrapper>
        <S.ResetPasswordContainer>
          <S.LinkToResetPassword href={forgetPassword.base}>
            Esqueceu a senha?
          </S.LinkToResetPassword>
        </S.ResetPasswordContainer>

        <Button
          fullWidth
          content="Entrar"
          type="submit"
          loading={isLoading}
          disabled={hasFormError}
        />
      </S.Form>

      <Modal
        open={modalExpiredPassword}
        setOpen={() => {
          return
        }}
        buttonAction={() => {
          navigate(URL_CONST.EXPIREDPASSWORD)
          setModalExpiredPassword(false)
        }}
        closeAction={() => setModalExpiredPassword(false)}
        title="Crie uma nova senha"
        description="Sua senha está expirada, você será redirecionado para uma página onde poderá atualizá-la."
        buttonText="Entendi"
      />
    </S.Container>
  )
}

export default LoginForm
